import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, ShieldCheck, Headset, UserCircle2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { EmailOtpLogin } from '@/components/EmailOtpLogin';
import { toast } from 'sonner';

/**
 * Role-aware login page.
 * Phase-1 of role-based entry routes:
 *   /admin/login      → role="admin"
 *   /telecaller/login → role="telecaller"
 *   /member/login     → role="member"
 *
 * Same backend (/api/auth/login), same JWT, same RBAC.
 * Only the UI, branding, and post-login routing differ.
 */

const PORTAL_CONFIG = {
  admin: {
    title: 'Admin Portal',
    subtitle: 'Sign in to manage the platform',
    identifierLabel: 'Mobile / Staff ID',
    identifierPlaceholder: 'Mobile number or Staff ID',
    Icon: ShieldCheck,
    accent: 'from-[#D4AF37] to-[#B8860B]',
    allowedRoles: ['super_admin', 'admin'],
    dashboardPath: '/admin',
    wrongRoleMessage: 'This portal is for administrators only.',
    showRegister: false,
    testIdPrefix: 'admin',
  },
  telecaller: {
    title: 'Telecaller / Executive Portal',
    subtitle: 'Sign in to your operations dashboard',
    identifierLabel: 'Mobile / Staff ID',
    identifierPlaceholder: 'Mobile or Staff ID (e.g. TEL0001)',
    Icon: Headset,
    accent: 'from-[#D4AF37] to-[#A07C2A]',
    allowedRoles: ['telecaller', 'admin', 'super_admin'],
    dashboardPath: '/telecaller',
    wrongRoleMessage: 'This portal is for staff (telecaller / executive) only.',
    showRegister: false,
    testIdPrefix: 'telecaller',
  },
  member: {
    title: 'Member Portal',
    subtitle: 'First time? Use your 10-digit Member ID as the password',
    identifierLabel: 'Mobile / Member ID',
    identifierPlaceholder: 'Mobile number or Member ID',
    Icon: UserCircle2,
    accent: 'from-[#D4AF37] to-[#8B6914]',
    allowedRoles: ['member'],
    dashboardPath: '/member',
    wrongRoleMessage: 'This portal is for members only.',
    showRegister: true,
    testIdPrefix: 'member',
  },
};

const roleToDashboard = (role) => {
  if (role === 'super_admin' || role === 'admin') return '/admin';
  if (role === 'telecaller') return '/telecaller';
  return '/member';
};

const RoleLoginPage = ({ role }) => {
  const cfg = PORTAL_CONFIG[role];
  const { login, logout, user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fresh = new URLSearchParams(location.search).has('fresh');

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ?fresh link: sign out any old session once, so the login form always shows.
  const freshHandled = useRef(false);
  useEffect(() => {
    if (loading || freshHandled.current) return;
    freshHandled.current = true;
    if (fresh && isAuthenticated) logout();
  }, [fresh, loading, isAuthenticated, logout]);

  // Already-logged-in users bypass login and go straight to their dashboard.
  if (!fresh && !loading && isAuthenticated && user?.role) {
    const back = location.state?.from;
    const dash = roleToDashboard(user.role);
    const target = back && back.pathname.startsWith(dash) && !/\/login$/.test(back.pathname) ? `${back.pathname}${back.search || ''}` : dash;
    return <Navigate to={target} replace />;
  }

  // Defensive: invalid role prop
  if (!cfg) {
    return <Navigate to="/login" replace />;
  }

  const { Icon } = cfg;
  // If a guarded route bounced us here, location.state.from may be the login URL itself
  // (e.g. /admin/login). Strip any *-login suffix so we don't redirect back to a login page.
  const rawFrom = location.state?.from ? `${location.state.from.pathname}${location.state.from.search || ''}` : cfg.dashboardPath;
  const from = /\/(login)$/.test(location.state?.from?.pathname || '') ? cfg.dashboardPath : rawFrom;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const loggedInUser = await login(identifier, password);

      // Wrong-portal guard: block role mismatch with a clear toast
      // then route the user to the correct dashboard for their actual role.
      if (!cfg.allowedRoles.includes(loggedInUser.role)) {
        toast.error(cfg.wrongRoleMessage, {
          description: 'Redirecting you to your correct dashboard…',
        });
        const correctPath = roleToDashboard(loggedInUser.role);
        // Keep submit disabled during the redirect window to avoid double-submit races.
        setTimeout(() => navigate(correctPath, { replace: true }), 900);
        return;
      }

      toast.success(`Welcome back, ${loggedInUser.name}!`);
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F0F10] flex items-center justify-center px-4 py-10"
      data-testid={`${cfg.testIdPrefix}-login-page`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1750763533241-7c2beccb9eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjbHViJTIwaW50ZXJpb3IlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzczMDgwNzc5fDA&ixlib=rb-4.1.0&q=85)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-gold p-8 md:p-10">
          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#D4AF37] mb-4 transition-colors"
            data-testid={`${cfg.testIdPrefix}-login-back-home`}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>

          {/* Brand + portal badge */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex flex-col items-center gap-2 mb-4">
              <img
                src="/brand/bci-logo-512.png"
                alt="BCI"
                className="w-16 h-16 object-contain drop-shadow-[0_8px_24px_rgba(212,175,55,0.35)]"
              />
              <span
                className="text-lg font-bold text-white tracking-wide"
                style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}
              >
                BITZ Club International
              </span>
            </Link>

            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${cfg.accent} text-black text-xs font-semibold tracking-wider uppercase mt-2`}
              data-testid={`${cfg.testIdPrefix}-login-portal-badge`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cfg.title}
            </div>

            <h1
              className="text-2xl md:text-3xl font-bold text-white mt-5 mb-1"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Welcome Back
            </h1>
            <p className="text-gray-400 text-sm">{cfg.subtitle}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="input-label">{cfg.identifierLabel}</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={cfg.identifierPlaceholder}
                className="input-gold"
                autoComplete="username"
                data-testid={`${cfg.testIdPrefix}-login-identifier`}
              />
            </div>

            <div>
              <label className="input-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input-gold pr-10"
                  autoComplete="current-password"
                  data-testid={`${cfg.testIdPrefix}-login-password`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white pr-3"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid={`${cfg.testIdPrefix}-login-toggle-password`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid={`${cfg.testIdPrefix}-login-submit`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {cfg.testIdPrefix === 'member' && (
            <EmailOtpLogin onDone={(u) => { toast.success(`Welcome back, ${u.name}!`); navigate(from, { replace: true }); }} />
          )}

          {cfg.testIdPrefix === 'member' && (
            <p className="mt-4 flex items-center justify-center gap-4 text-sm">
              <Link to="/change-password" className="text-[#D4AF37] hover:underline" data-testid="member-login-change-password-link">
                Change Password
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/forgot-password" className="text-[#D4AF37] hover:underline" data-testid="member-login-forgot-password-link">
                Forgot password?
              </Link>
            </p>
          )}

          {/* Footer — member-only register CTA. Cross-portal navigation intentionally
              removed: members enter via the homepage CTA; staff URLs are internal-only. */}
          {cfg.showRegister && (
            <div className="mt-7 pt-5 border-t border-white/5 text-center">
              <p className="text-gray-400 text-sm">
                Don&apos;t have an account?{' '}
                <Link
                  to="/register"
                  className="text-[#D4AF37] hover:underline"
                  data-testid={`${cfg.testIdPrefix}-login-register-link`}
                >
                  Join Now
                </Link>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RoleLoginPage;

// Convenience named wrappers so App.js stays clean.
export const AdminLoginPage = () => <RoleLoginPage role="admin" />;
export const TelecallerLoginPage = () => <RoleLoginPage role="telecaller" />;
export const MemberLoginPage = () => <RoleLoginPage role="member" />;
